import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { Wallet, Mail, AlertCircle } from "lucide-react";

export const ConnectWallet = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Connecting Your Wallet</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Learn how to connect your Solana wallet or sign in with email/social login</p>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Overview</h2>
          <p className="text-muted-foreground mb-6">
            To start trading on Nordex, you'll need to connect a wallet. We support two connection methods:
          </p>
          
          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Wallet className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Solana Wallet</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Connect directly with browser extension wallets like Phantom, Solflare, or Backpack. 
                Full control of your private keys.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Mail className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-lg">Email/Social Login</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Sign in with email, Google, X, Apple, or other social accounts via Privy. 
                No wallet extension required.
              </p>
            </div>
          </div>
        </section>

        {/* Method 1: Solana Wallet */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Method 1: Solana Wallet Connection</h2>
          
          <h3 className="text-lg font-semibold mb-4">Supported Wallets</h3>
          <ul className="list-disc pl-6 mb-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Phantom</strong> - Most popular Solana wallet with browser extension and mobile app
            </li>
            <li>
              <strong className="text-foreground">Solflare</strong> - Feature-rich wallet with staking support
            </li>
            <li>
              <strong className="text-foreground">Backpack</strong> - Modern wallet with clean interface
            </li>
            <li>
              <strong className="text-foreground">Any Wallet Adapter Compatible Wallet</strong> - Other Solana wallets that support the standard wallet adapter
            </li>
          </ul>

          <h3 className="text-lg font-semibold mb-4">Connection Steps</h3>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h4 className="font-semibold mb-1">Install a Solana Wallet</h4>
                <p className="text-sm text-muted-foreground">
                  If you don't have a wallet yet, download and install Phantom, Solflare, or Backpack 
                  from their official websites. Create a new wallet and securely store your seed phrase.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h4 className="font-semibold mb-1">Click "Select Wallet"</h4>
                <p className="text-sm text-muted-foreground">
                  On the Nordex homepage, click the "Select Wallet" button in the top right corner of the screen.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h4 className="font-semibold mb-1">Choose Your Wallet</h4>
                <p className="text-sm text-muted-foreground">
                  A modal will appear showing available wallet options. Select your wallet from the list.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <h4 className="font-semibold mb-1">Approve Connection</h4>
                <p className="text-sm text-muted-foreground">
                  Your wallet will prompt you to approve the connection. Review the permissions and click approve.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                5
              </div>
              <div>
                <h4 className="font-semibold mb-1">Connected!</h4>
                <p className="text-sm text-muted-foreground">
                  Once connected, you'll see your wallet address displayed in the header. You're now ready to deposit funds and start trading.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Method 2: Email/Social Login */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Method 2: Email/Social Login (Privy)</h2>
          
          <p className="text-muted-foreground mb-6">
            Don't have a Solana wallet? No problem! You can sign in with your email or social accounts.
            Privy will create a secure wallet for you automatically.
          </p>

          <h3 className="text-lg font-semibold mb-4">Connection Steps</h3>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h4 className="font-semibold mb-1">Click "Select Wallet"</h4>
                <p className="text-sm text-muted-foreground">
                  Click the "Select Wallet" button in the top right corner.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h4 className="font-semibold mb-1">Choose Email/Social Login</h4>
                <p className="text-sm text-muted-foreground">
                  Select "Login with Email" or one of the social login options (Google, X, Apple, etc.).
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h4 className="font-semibold mb-1">Complete Authentication</h4>
                <p className="text-sm text-muted-foreground">
                  Follow the prompts to verify your email or authenticate with your social account.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <h4 className="font-semibold mb-1">Wallet Created Automatically</h4>
                <p className="text-sm text-muted-foreground">
                  Privy will automatically create a Solana wallet for you. You can export the private key later if needed.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Troubleshooting</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-primary" />
                Wallet Won't Connect
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 pl-6 list-disc">
                <li>Make sure you have a Solana wallet extension installed</li>
                <li>Try refreshing the page</li>
                <li>Check that your wallet is unlocked</li>
                <li>Disable other wallet extensions that might conflict</li>
              </ul>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-primary" />
                Wrong Network
              </h3>
              <p className="text-sm text-muted-foreground">
                Ensure your wallet is connected to Solana Mainnet. If your wallet is set to a different 
                network (like Devnet or Testnet), switch it to Mainnet in your wallet settings.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-primary" />
                Multiple Wallets Detected
              </h3>
              <p className="text-sm text-muted-foreground">
                If you have multiple wallet extensions installed, make sure to select the correct one 
                from the wallet selector modal.
              </p>
            </div>
          </div>
        </section>

        <DocsNavigation 
          previous={{ title: "Welcome to Nordex", href: "/welcome" }}
          next={{ title: "Making Your First Deposit", href: "/first-deposit" }}
        />
      </div>
    </DocsLayout>
  );
};

